import { apiClient } from '../utils/api-client';

interface StartImpersonationRequest {
  contact_id: string;
  investor_id: string;
}

interface StartImpersonationResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  session_id: string;
  impersonation_log_id: string;
}

interface EndImpersonationResponse {
  message: string;
  ended_at: string;
}

interface ImpersonationLog {
  id: string;
  admin_user_id: string;
  admin_user_email: string;
  admin_user_name: string;
  contact_id: string;
  contact_name: string;
  contact_email: string | null;
  investor_id: string;
  investor_name: string;
  session_id: string;
  started_at: string;
  ended_at: string | null;
  is_active: boolean;
  duration_seconds: number | null;
  ip_address: string | null;
  user_agent: string | null;
}

interface ImpersonationListResponse {
  items: ImpersonationLog[];
  total: number;
}

class ImpersonationService {
  private baseUrl = '/impersonation';

  /**
   * Start impersonating an investor portal contact.
   * Returns tokens that can be used to access the portal.
   * Store cookie httponly refresh token
   */
  async startImpersonation(
    request: StartImpersonationRequest
  ): Promise<StartImpersonationResponse> {
    return apiClient.post(`${this.baseUrl}/start`, request, {
      credentials: 'include',
    });
  }

  /**
   * End an active impersonation session.
   */
  async endImpersonation(sessionId: string): Promise<EndImpersonationResponse> {
    return apiClient.post(`${this.baseUrl}/end/${sessionId}`, {});
  }

  /**
   * Get list of active impersonation sessions.
   */
  async getActiveImpersonations(
    mySessionsOnly = false
  ): Promise<ImpersonationListResponse> {
    const params = new URLSearchParams();
    if (mySessionsOnly) {
      params.append('my_sessions_only', 'true');
    }
    return apiClient.get(`${this.baseUrl}/active?${params.toString()}`);
  }

  /**
   * Get impersonation history with optional filters.
   */
  async getImpersonationHistory(options?: {
    admin_user_id?: string;
    contact_id?: string;
    investor_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ImpersonationListResponse> {
    const params = new URLSearchParams();
    if (options?.admin_user_id) {
      params.append('admin_user_id', options.admin_user_id);
    }
    if (options?.contact_id) {
      params.append('contact_id', options.contact_id);
    }
    if (options?.investor_id) {
      params.append('investor_id', options.investor_id);
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.offset) {
      params.append('offset', options.offset.toString());
    }
    return apiClient.get(`${this.baseUrl}/history?${params.toString()}`);
  }

  /**
   * Open the investor portal with impersonation tokens.
   * Stores tokens in localStorage and opens portal in new tab.
   * @param tokens The tokens from the start impersonation response
   * @param redirectPath The path to redirect to after handoff (default: /portal)
   */
  openPortalWithImpersonation(
    tokens: StartImpersonationResponse,
    redirectPath = '/portal'
  ): void {
    // Store the handoff payload for the portal tab.
    const handoffData = {
      access_token: tokens.access_token,
      expires_in: tokens.expires_in,
      session_id: tokens.session_id,
      redirect_path: redirectPath,
      timestamp: Date.now(),
    };

    localStorage.setItem('impersonation_handoff', JSON.stringify(handoffData));

    // Open portal in new tab with handoff parameter
    const portalUrl = this.getPortalUrl();
    window.open(`${portalUrl}/portal/auth/impersonation-handoff`, '_blank');
  }

  /**
   * Get the portal URL based on environment.
   */
  private getPortalUrl(): string {
    // The portal is served from the same origin under /portal in every
    // environment. Change this if it ever moves to its own subdomain.
    return window.location.origin;
  }
}

export const impersonationService = new ImpersonationService();
